import { memo, useCallback, useEffect, useState, useMemo } from 'react'
import type { ReactNode } from 'react'
import { Image, Lock, LockOpen, Radio, Shapes, SkipForward, Square, Stethoscope } from 'lucide-react'
import { useStore, useAlerts, useHasPermission } from '../../store/index.js'
import { usePlayout } from '../../hooks/usePlayout.js'
import { useOnAirState } from '../../hooks/useOnAirState.js'
import type { ScheduleItem, WatchdogAlert } from '../../types/index.js'
import {
  playoutApi,
  type EndLivePlan,
  type LiveBreakRequest,
  type LiveBreakTemplate,
  type LiveFillEntry,
} from '../../lib/api.js'
import { canTakeWaitingLiveEarly, listWaitingLives, resolveLiveBreakState } from '../../lib/liveBreakState.js'
import { broadcastDayKey } from '../../lib/broadcast-day.js'
import { LiveSkipDialog } from './LiveSkipDialog.js'
import { LiveBreakDialog } from './LiveBreakDialog.js'
import { LiveControlBanner } from './LiveControlBanner.js'
import { LivePartChrono } from './LivePartChrono.js'
import { LiveResumeBanner } from './LiveResumeBanner.js'
import { LiveStartNowBanner } from './LiveStartNowBanner.js'
import { findStartableLive } from '../../lib/liveStartNow.js'
import { templateFillEntries } from '../../lib/liveBreakTemplate.js'
import { useServerClock } from '../../hooks/useServerClock.js'
import { AntennaReturn } from './AntennaReturn.js'
import { BrandingPanel } from './BrandingPanel.js'
import './MonitorPanel.css'

// ─── Props ────────────────────────────────────────────────────────────────────

interface MonitorPanelProps {
  channelId:      string | null
}

// ─── Composant principal ──────────────────────────────────────────────────────
//
// Rail antenne de la Grille. L'élément à l'antenne, l'élément suivant et
// l'habillage sont désormais portés par le bandeau antenne permanent
// (OnAirBanner) ; le diagnostic playout a rejoint Réglages → Watchdog. Le rail
// garde ce qui n'a de sens qu'ici : le retour antenne, les alertes et les
// commandes du direct et d'urgence. La playlist du jour en a été retirée — la
// grille l'affiche déjà — pour rendre sa place aux commandes du direct.

export const MonitorPanel = memo(function MonitorPanel({ channelId }: MonitorPanelProps) {
  const alerts = useAlerts()
  // Les contrôles antenne (habillage, skip, interlude, logo, arrêt) ne sont
  // pilotables qu'avec PLAYOUT_CONTROL. Sans cette permission (rôle Journaliste),
  // on les masque plutôt que de les afficher désactivés.
  const canControlPlayout = useHasPermission('PLAYOUT_CONTROL')
  const { onAirItem, nextItem } = useOnAirState()
  const {
    playoutState, endLiveAndFill, goLive, toggleInterlude, emergencyStop, toggleBug,
    breakLive, resumeLive, takeLive, startLiveNow,
    skipPending, goLivePending, endLivePending, breakPending, interludePending, emergencyPending,
    bugPending, activeBugType,
    manualInterludeActive,
  } = usePlayout(channelId)

  const bridgeConnected = playoutState?.bridgeConnected ?? false

  // Verrouillage des contrôles d'urgence — verrouillé par défaut
  const [controlsLocked, setControlsLocked] = useState(true)
  const [alertsExpanded, setAlertsExpanded] = useState(true)
  const [liveSkipOpen, setLiveSkipOpen] = useState(false)
  // Plan de coupure proposé par le serveur quand la boîte est ouverte depuis
  // « Fin du direct ». `null` = ouverture depuis le skip classique, sélection vide.
  const [endPlan, setEndPlan] = useState<EndLivePlan | null>(null)
  const [endPlanLoading, setEndPlanLoading] = useState(false)
  const [breakOpen, setBreakOpen] = useState(false)
  // Décrochage préfait de la journée d'antenne, chargé dès qu'un direct est à
  // l'antenne : c'est lui que « Décrocher (modèle) » pose sans autre question.
  const [breakTemplate, setBreakTemplate] = useState<LiveBreakTemplate | null>(null)

  const items = useStore(state => state.items)
  // Décrochage en cours : reconnu sur la seule grille du jour
  // (`lib/liveBreakState.ts`), pour que « Raccrocher » apparaisse même si
  // l'opérateur vient d'ouvrir l'écran.
  const breakState = useMemo(
    () => resolveLiveBreakState(items, onAirItem ?? null),
    [items, onAirItem],
  )
  // Direct suivant d'un autre bloc, qui attend la fin du décrochage.
  const waitingLive = useMemo(
    () => (breakState.breaking ? listWaitingLives(items, breakState.brokenFrom)[0] ?? null : null),
    [items, breakState.breaking, breakState.brokenFrom],
  )
  // Prochaine ancre : il peut être pris avant son heure de grille. Recalculé à
  // chaque changement de grille ; le bandeau, qui bat à la seconde, n'offre le
  // geste que tant que l'heure n'est pas passée.
  const waitingLiveEarly = useMemo(
    () => !!waitingLive && !!onAirItem
      && canTakeWaitingLiveEarly(items, waitingLive, onAirItem, new Date(onAirItem.startTime).getTime()),
    [items, waitingLive, onAirItem],
  )

  const handleToggleAlerts = useCallback(() => setAlertsExpanded(current => !current), [])
  const handleClearAlerts = useCallback(() => {
    const store = useStore.getState()
    store.clearAlerts()
    store.markAlertsRead()
  }, [])
  const handleDismissAlert = useCallback((type: string, itemId?: string) => {
    useStore.getState().dismissAlert(type, itemId)
  }, [])
  const handleToggleInterlude = useCallback(() => { void toggleInterlude() }, [toggleInterlude])
  const handleToggleLogo = useCallback(() => { void toggleBug() }, [toggleBug])
  const handleToggleLock = useCallback(() => setControlsLocked(value => !value), [])
  const handleGoLive = useCallback(() => {
    if (onAirItem) void goLive(onAirItem.id).catch(() => {})
  }, [goLive, onAirItem])
  // Le décrochage préfait est relu à chaque changement d'élément à l'antenne :
  // la journée d'antenne peut avoir tourné (elle va de 22:00 UTC à 22:00 UTC) et
  // l'opérateur peut l'avoir modifié depuis la grille entre-temps.
  const onAirDayKey = onAirItem ? broadcastDayKey(onAirItem.startTime) : null
  useEffect(() => {
    if (!channelId || !onAirDayKey) { setBreakTemplate(null); return }
    let cancelled = false
    playoutApi.getLiveBreakTemplate(channelId, onAirDayKey)
      .then(res => { if (!cancelled) setBreakTemplate(res) })
      .catch(() => { if (!cancelled) setBreakTemplate(null) })
    return () => { cancelled = true }
  }, [channelId, onAirDayKey])

  const handleOpenBreak = useCallback(() => setBreakOpen(true), [])
  const handleCancelBreak = useCallback(() => setBreakOpen(false), [])

  /** Décrochage composé dans la boîte : le serveur reste seul juge du calage. */
  const handleConfirmBreak = useCallback(async (body: Omit<LiveBreakRequest, 'itemId'>) => {
    if (!onAirItem) return
    await breakLive({ itemId: onAirItem.id, ...body })
    setBreakOpen(false)
  }, [breakLive, onAirItem])

  /**
   * Décrochage immédiat sur l'infoservice, sans rien composer : mode sans durée
   * fixe, infoservice élastique. L'habillage d'infoservice s'arme sur toute la
   * plage, et le direct revient par « Reprendre le direct ».
   */
  const handleBreakInfoservice = useCallback(() => {
    if (!onAirItem) return
    void breakLive({
      itemId: onAirItem.id,
      mode:   'OPEN',
      resume: true,
      items:  [{ itemType: 'INFOSERVICE', fillRest: true }],
    }).catch(() => {})
  }, [breakLive, onAirItem])

  /** Décrochage préfait : posé tel quel, sans ouvrir la composition. */
  const handleBreakWithTemplate = useCallback(() => {
    if (!onAirItem || !breakTemplate) return
    void breakLive({
      itemId: onAirItem.id,
      mode:   breakTemplate.mode,
      resume: true,
      // L'infoservice d'un modèle comble le reste de la durée donnée, y compris
      // sur un modèle enregistré avant cette règle (`lib/liveBreakTemplate.ts`).
      items:  templateFillEntries(breakTemplate.items),
      ...(breakTemplate.mode === 'FIXED' && breakTemplate.windowSec
        ? { windowSec: breakTemplate.windowSec }
        : {}),
    }).catch(() => {})
  }, [breakLive, breakTemplate, onAirItem])

  const handleResumeLive = useCallback(() => {
    void resumeLive(breakState.resumeItem?.id).catch(() => {})
  }, [resumeLive, breakState.resumeItem])
  const handleTakeLive = useCallback((item: ScheduleItem, early: boolean) => {
    void takeLive(item.id, early).catch(() => {})
  }, [takeLive])
  const handleStartLiveNow = useCallback((item: ScheduleItem) => {
    void startLiveNow(item.id).catch(() => {})
  }, [startLiveNow])

  // Direct du créneau qui n'est pas à l'antenne : en ERREUR, ou resté en attente
  // après son heure. Ses gestes de direct seraient tous refusés, la régie
  // propose de lancer la source (`lib/liveStartNow.ts`).
  const { nowMs } = useServerClock()
  const startableLive = useMemo(
    () => (breakState.breaking ? null : findStartableLive(items, nowMs)),
    [items, nowMs, breakState.breaking],
  )

  /**
   * « Fin du direct » n'applique plus la coupure à l'aveugle : on demande au
   * serveur le plan qu'il poserait, puis on l'ouvre dans la boîte de choix,
   * pré-remplie. L'opérateur valide tel quel ou remplace le contenu du trou
   * qu'il crée en sortant plus tôt.
   */
  const handleEndLive = useCallback(async () => {
    if (!onAirItem || !channelId || endPlanLoading) return
    setEndPlanLoading(true)
    try {
      setEndPlan(await playoutApi.endLivePlan(channelId, onAirItem.id))
      setLiveSkipOpen(true)
    } catch {
      // Le plan est indisponible (direct qui vient de quitter l'antenne, réseau) :
      // on ouvre quand même la boîte, avec une sélection vide plutôt que rien.
      setEndPlan(null)
      setLiveSkipOpen(true)
    } finally {
      setEndPlanLoading(false)
    }
  }, [channelId, endPlanLoading, onAirItem])
  const handleEndLiveClick = useCallback(() => { void handleEndLive() }, [handleEndLive])

  // Un seul bandeau porte tous les états du direct, juste au-dessus des quatre
  // boutons principaux. Le moteur doit avoir désigné le direct manuel en attente :
  // comparer les deux identifiants ne suffit pas, car hors ligne il n'y a ni
  // élément à l'antenne ni état playout, et `undefined === undefined` affichait
  // « Passer au direct » sur une antenne éteinte.
  const liveOnAir = onAirItem?.itemType === 'LIVE' && onAirItem.id !== startableLive?.id ? onAirItem : null

  // Bloc dont le chrono de parties parle : celui à l'antenne, ou celui dont on
  // vient de décrocher — c'est pendant la coupure que le déroulé est le plus
  // utile, et c'est là que la reprise n'est pas encore à l'antenne.
  const livePartItem = liveOnAir ?? (breakState.breaking ? breakState.brokenFrom : null)
  const awaitingLiveStart = playoutState?.pendingManualLiveItemId != null
    && onAirItem?.id === playoutState.pendingManualLiveItemId

  // ── Skip avec aperçu du trou et remplissage ─────────────────────────────────
  const handleSkipClick = useCallback(() => { setEndPlan(null); setLiveSkipOpen(true) }, [])

  const handleLiveSkipConfirm = useCallback(async (
    fillItems: LiveFillEntry[],
    options: { compact: boolean; leadSec?: number; cutAt?: string; requireExactFill?: boolean },
  ) => {
    if (!onAirItem) return
    await endLiveAndFill(onAirItem.id, fillItems, options)
    setLiveSkipOpen(false)
    setEndPlan(null)
  }, [onAirItem, endLiveAndFill])

  const handleLiveSkipCancel = useCallback(() => { setLiveSkipOpen(false); setEndPlan(null) }, [])

  const activeAlertCount = useMemo(() => alerts.filter(alert => alert.severity !== 'OK').length, [alerts])

  return (
    <div className="tp-monitor">
      <div className="tp-monitor-scroll">
        <AntennaReturn />

        {canControlPlayout && (
          <section className="monitor-branding" aria-label="Habillage antenne">
            <BrandingPanel channelId={channelId} onAirItem={onAirItem} />
          </section>
        )}

        <CollapsibleSection
          label="Alertes Antenne"
          badge={activeAlertCount || undefined}
          expanded={alertsExpanded}
          onToggle={handleToggleAlerts}
          onClear={alerts.length ? handleClearAlerts : undefined}
        >
          <AlertSection alerts={alerts} onDismiss={handleDismissAlert} />
        </CollapsibleSection>
      </div>

      {/* CONTRÔLES — regroupés pour que le volet, posé sous la grille sur
          tablette, puisse les mettre à côté du retour antenne. */}
      <div className="tp-monitor-controls">
      {canControlPlayout && <>
      {breakState.breaking && breakState.brokenFrom && (
        <LiveResumeBanner
          brokenFrom={breakState.brokenFrom}
          resumeItem={breakState.resumeItem}
          waitingLive={waitingLive}
          canTakeEarly={waitingLiveEarly}
          disabled={controlsLocked || !bridgeConnected}
          pending={breakPending}
          onResume={handleResumeLive}
          onTake={handleTakeLive}
        />
      )}
      {startableLive && (
        <LiveStartNowBanner
          item={startableLive}
          disabled={controlsLocked || !bridgeConnected}
          pending={goLivePending}
          onStart={handleStartLiveNow}
        />
      )}
      {/* Le déroulé se lit avant les gestes : il dit où on en est, le bandeau
          dit ce qu'on peut faire. Il reste affiché pendant le décrochage —
          compteur effacé, partie suivante annoncée — sans quoi la régie perdrait
          le déroulé au moment précis où elle prépare le raccrochage. */}
      {livePartItem && <LivePartChrono item={livePartItem} items={items} adjustable />}
      {liveOnAir && channelId && (
        <LiveControlBanner
          channelId={channelId}
          onAirItem={liveOnAir}
          awaitingStart={awaitingLiveStart}
          disabled={controlsLocked || !bridgeConnected}
          goLivePending={goLivePending}
          endLivePending={endLivePending || endPlanLoading}
          onGoLive={handleGoLive}
          onEndLive={handleEndLiveClick}
          onBreak={handleOpenBreak}
          onBreakInfoservice={handleBreakInfoservice}
          onBreakWithTemplate={handleBreakWithTemplate}
          breakPending={breakPending}
          hasTemplate={(breakTemplate?.items.length ?? 0) > 0}
        />
      )}
      <ControlsBar
        onSkip={handleSkipClick}
        onInterlude={handleToggleInterlude}
        onEmergency={emergencyStop}
        onToggleBug={handleToggleLogo}
        skipPending={skipPending}
        interludePending={interludePending}
        emergencyPending={emergencyPending}
        bugPending={bugPending}
        bugVisible={!!activeBugType}
        manualInterludeActive={manualInterludeActive}
        locked={controlsLocked}
        onToggleLock={handleToggleLock}
        hasNextItem={!!nextItem}
        bridgeConnected={bridgeConnected}
      />
      </>}

      <p className="monitor-diagnostic-pointer">
        <Stethoscope size={13} aria-hidden="true" />
        Diagnostic playout (dérive, AMCP, moteur, recovery) : Réglages → Watchdog
      </p>
      </div>

      {/* ── Dialog de remplissage au skip d'un direct ── */}
      {liveSkipOpen && channelId && onAirItem && (
        <LiveSkipDialog
          channelId={channelId}
          liveItem={onAirItem}
          nextItem={nextItem}
          initialPlan={endPlan}
          onConfirm={handleLiveSkipConfirm}
          onCancel={handleLiveSkipCancel}
        />
      )}

      {breakOpen && channelId && liveOnAir && (
        <LiveBreakDialog
          channelId={channelId}
          liveItem={liveOnAir}
          nextItem={nextItem}
          initialMode={breakTemplate?.mode ?? 'FIXED'}
          initialWindowSec={breakTemplate?.windowSec ?? null}
          onConfirm={handleConfirmBreak}
          onCancel={handleCancelBreak}
        />
      )}
    </div>
  )
})

// ─── Sous-composants ──────────────────────────────────────────────────────────

const CollapsibleSection = memo(function CollapsibleSection({
  label,
  badge,
  expanded,
  onToggle,
  onClear,
  children,
}: {
  label: string
  badge?: number
  expanded: boolean
  onToggle: () => void
  onClear?: () => void
  children: ReactNode
}) {
  return (
    <section className="monitor-section">
      <div className="tp-panel-header">
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={expanded}
          className="tp-panel-toggle"
        >
          <span className="tp-panel-chevron" aria-hidden="true">
            {expanded ? '−' : '+'}
          </span>
          <span className="tp-panel-title">
            {label}
          </span>
          {badge !== undefined && badge > 0 && (
            <span className="monitor-section__badge">
              {badge}
            </span>
          )}
        </button>
        {onClear && (
          <button
            type="button"
            onClick={onClear}
            title="Effacer les alertes"
            className="monitor-section__clear"
          >
            Effacer
          </button>
        )}
      </div>
      {expanded && (
        <div className="monitor-section__body">
          {children}
        </div>
      )}
    </section>
  )
})

const AlertSection = memo(function AlertSection({
  alerts,
  onDismiss,
}: {
  alerts: WatchdogAlert[]
  onDismiss: (type: string, itemId?: string) => void
}) {
  const activeAlerts = alerts.filter(alert => alert.severity !== 'OK')

  return (
    <div className="monitor-alerts">
      {activeAlerts.length === 0 ? (
        <div className="monitor-empty">Aucune alerte active</div>
      ) : (
        activeAlerts.slice(0, 6).map(alert => (
          <button
            key={`${alert.type}:${alert.itemId ?? 'global'}`}
            type="button"
            onClick={() => onDismiss(alert.type, alert.itemId)}
            title="Cliquer pour retirer l'alerte"
            className={`monitor-alert ${alert.severity === 'WARNING' ? 'is-warning' : 'is-danger'}`}
          >
            <div className="monitor-alert__meta">
              <strong>{alert.severity}</strong>
              <span>{new Date(alert.ts).toLocaleTimeString()}</span>
            </div>
            <div className="monitor-alert__message">{alert.message}</div>
          </button>
        ))
      )}
    </div>
  )
})

const ControlsBar = memo(function ControlsBar({
  onSkip, onInterlude, onEmergency, onToggleBug,
  skipPending, interludePending, emergencyPending, bugPending, bugVisible,
  manualInterludeActive,
  locked, onToggleLock, hasNextItem,
  bridgeConnected,
}: {
  onSkip: () => void; onInterlude: () => void; onEmergency: () => void; onToggleBug: () => void
  skipPending: boolean; interludePending: boolean; emergencyPending: boolean; bugPending: boolean; bugVisible: boolean
  manualInterludeActive: boolean
  locked: boolean; onToggleLock: () => void; hasNextItem: boolean
  bridgeConnected: boolean
}) {
  const skipDisabled  = locked || skipPending || !hasNextItem
  const otherDisabled = locked || !bridgeConnected

  return (
    <div className="monitor-controls">
      <div className="monitor-controls__lock-row">
        <button
          onClick={onToggleLock}
          className={`tp-button monitor-controls__lock ${locked ? 'tp-button--warning' : 'tp-button--info'}`}
          aria-pressed={!locked}
        >
          {locked
            ? <Lock size={13} aria-hidden="true" />
            : <LockOpen size={13} aria-hidden="true" />}
          <span>{locked ? 'Contrôles verrouillés' : 'Contrôles actifs'}</span>
        </button>
      </div>

      <div className="monitor-controls__buttons">
        <button
          className={`tp-button monitor-controls__button ${bugVisible ? 'is-active' : ''}`}
          disabled={otherDisabled || bugPending}
          onClick={onToggleBug}
          aria-pressed={bugVisible}
        >
          <Image size={14} aria-hidden="true" />
          {bugPending ? '…' : 'Logo'}
        </button>

        <button
          className="tp-button monitor-controls__button"
          disabled={skipDisabled}
          onClick={onSkip}
        >
          <SkipForward size={14} aria-hidden="true" />
          {skipPending ? '…' : 'Skip'}
        </button>

        <button
          className={`tp-button monitor-controls__button ${manualInterludeActive ? 'is-active' : ''}`}
          disabled={otherDisabled || interludePending}
          onClick={onInterlude}
          aria-pressed={manualInterludeActive}
        >
          <Shapes size={14} aria-hidden="true" />
          {interludePending ? '…' : 'Interlude'}
        </button>

        <button
          className="tp-button tp-button--critical monitor-controls__button"
          disabled={otherDisabled || emergencyPending}
          onClick={onEmergency}
        >
          <Square size={14} aria-hidden="true" />
          {emergencyPending ? '…' : 'Arrêt'}
        </button>
      </div>
    </div>
  )
})
